import { index, pgTable, text, timestamp, uniqueIndex, uuid } from 'drizzle-orm/pg-core';
import { applications } from './applications.ts';

/**
 * Associações de ACESSO das aplicações (unidade / setor / colaborador).
 *
 * Modelagem: as três tabelas guardam apenas a REFERÊNCIA LÓGICA do TOTVS
 * (`cod_filial`, `setor_nome`, `matricula`) — deliberadamente SEM FK para
 * `totvs_collaborators`, porque o espelho é apagado e recriado pelo job de sync
 * (services/totvs-sync.ts): uma FK derrubaria o sync ou apagaria acesso em
 * cascata a cada demissão/reprocessamento. O `nome` é um SNAPSHOT do momento do
 * cadastro, guardado só para a UI conseguir exibir a seleção mesmo que a pessoa
 * ou a razão social mude/saia do espelho.
 *
 * A FK é para `applications.id` com `onDelete: 'cascade'` — apagar a app apaga
 * o acesso. A escrita é sempre delete+insert dentro de uma transação
 * (routes/applications.ts), nunca parcial.
 */
export const applicationUnits = pgTable(
  'application_units',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    applicationId: uuid('application_id')
      .notNull()
      .references(() => applications.id, { onDelete: 'cascade' }),
    // Código da filial no TOTVS (o mesmo código pode ter várias razões sociais).
    codFilial: text('cod_filial').notNull(),
    // Snapshot da razão social representativa na hora do cadastro.
    nome: text('nome'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex('application_units_app_unit_uq').on(t.applicationId, t.codFilial),
    index('application_units_cod_filial_idx').on(t.codFilial),
  ],
);

export const applicationSectors = pgTable(
  'application_sectors',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    applicationId: uuid('application_id')
      .notNull()
      .references(() => applications.id, { onDelete: 'cascade' }),
    // Nome do setor (NOMESECAO) — o código da seção não é estável entre filiais.
    setorNome: text('setor_nome').notNull(),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex('application_sectors_app_sector_uq').on(t.applicationId, t.setorNome),
    index('application_sectors_setor_nome_idx').on(t.setorNome),
  ],
);

export const applicationCollaborators = pgTable(
  'application_collaborators',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    applicationId: uuid('application_id')
      .notNull()
      .references(() => applications.id, { onDelete: 'cascade' }),
    // Matrícula (CHAPA) — mesma referência lógica usada em `users.matricula`.
    matricula: text('matricula').notNull(),
    // Snapshot do nome na hora do cadastro.
    nome: text('nome'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex('application_collaborators_app_matricula_uq').on(t.applicationId, t.matricula),
    index('application_collaborators_matricula_idx').on(t.matricula),
  ],
);

export type ApplicationUnitRow = typeof applicationUnits.$inferSelect;
export type NewApplicationUnit = typeof applicationUnits.$inferInsert;
export type ApplicationSectorRow = typeof applicationSectors.$inferSelect;
export type NewApplicationSector = typeof applicationSectors.$inferInsert;
export type ApplicationCollaboratorRow = typeof applicationCollaborators.$inferSelect;
export type NewApplicationCollaborator = typeof applicationCollaborators.$inferInsert;
